/*
 * UTM + landing capture — site-wide, first-touch within the browsing session.
 *
 * utm_* params usually live only on the entry URL; they're dropped the moment a
 * visitor clicks an internal link. So we capture them on the FIRST page of the
 * session that carries them and stash them in sessionStorage (first-touch — later
 * pages never overwrite). The landing page (first location.href of the session)
 * is stored on the very first page load regardless of utm presence. Both live for
 * the tab session only (cleared when the tab closes).
 *
 * Exposes window.bqGetUtm() -> object (possibly empty) and window.bqGetLanding()
 * -> string|null, read by assets/lead-form.js at submit time so attribution
 * survives navigation away from the landing URL.
 *
 * Also exposes window.bqGetFbp() / window.bqGetFbc() — the Meta browser identifiers
 * our forms forward to the Conversions API (server-side Lead/Contact events). They
 * live here because they are the same problem as utm_*: the value arrives on the
 * entry URL and has to survive navigation. See ops/plans/meta-conversions-api.md.
 */
(function () {
  var UTM_KEY = 'bq_utm';
  var LANDING_KEY = 'bq_landing';
  var FBCLID_KEY = 'bq_fbclid';
  var REFERRER_KEY = 'bq_referrer';

  function ss() {
    try { return window.sessionStorage; } catch (e) { return null; } // private mode
  }

  function parseUtmFromUrl() {
    var utm = {};
    try {
      new URLSearchParams(window.location.search).forEach(function (v, k) {
        if (k.lastIndexOf('utm_', 0) === 0 && v) utm[k] = String(v).slice(0, 200);
      });
    } catch (e) { /* no URLSearchParams */ }
    return utm;
  }

  var store = ss();

  // First-touch UTM: only write if nothing's stored yet and this page carries utm_*.
  if (store && store.getItem(UTM_KEY) == null) {
    var utm = parseUtmFromUrl();
    if (Object.keys(utm).length) {
      try { store.setItem(UTM_KEY, JSON.stringify(utm)); } catch (e) { /* quota */ }
    }
  }

  // First-touch landing: first page of the session, set once.
  if (store && store.getItem(LANDING_KEY) == null) {
    try { store.setItem(LANDING_KEY, String(window.location.href).slice(0, 2000)); } catch (e) { /* */ }
  }

  // First-touch EXTERNAL referrer — who sent them, when nobody tagged the link.
  // ChatGPT appends ?utm_source=chatgpt.com and is caught by the utm block above, but
  // most AI answers and many organic surfaces (Perplexity, Google AI overviews, forum
  // posts) link without any utm at all. document.referrer is the only trace of those,
  // and it survives exactly one navigation — hence first-touch, same as utm.
  // Our own pages are skipped so internal navigation can't overwrite the real source.
  if (store && store.getItem(REFERRER_KEY) == null) {
    try {
      var ref = String(document.referrer || '');
      if (ref && ref.indexOf('://' + window.location.host) === -1) {
        store.setItem(REFERRER_KEY, ref.slice(0, 2000));
      }
    } catch (e) { /* quota / blocked */ }
  }

  window.bqGetUtm = function () {
    if (!store) return {};
    try {
      var raw = store.getItem(UTM_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) { return {}; }
  };
  window.bqGetLanding = function () {
    if (!store) return null;
    try { return store.getItem(LANDING_KEY) || null; } catch (e) { return null; }
  };
  window.bqGetReferrer = function () {
    if (!store) return null;
    try { return store.getItem(REFERRER_KEY) || null; } catch (e) { return null; }
  };

  // --- Meta identifiers (_fbp / _fbc) -------------------------------------------------
  //
  // The Meta pixel is installed by the Facebook & Instagram sales channel and runs in
  // the page's own context (runtimeContext: OPEN), so both cookies are first-party on
  // barequip.pro and readable from here.

  // First-touch fbclid, same reasoning as utm_*: an ad click lands with ?fbclid= on one
  // page, and it's gone the moment the visitor clicks through to the form.
  // Stored as "<clickTimestamp>|<fbclid>" — Meta's fbc format wants the time the CLICK
  // happened, which is now, not whenever the visitor gets around to submitting a form.
  if (store && store.getItem(FBCLID_KEY) == null) {
    try {
      var fbclid = new URLSearchParams(window.location.search).get('fbclid');
      if (fbclid) store.setItem(FBCLID_KEY, Date.now() + '|' + String(fbclid).slice(0, 400));
    } catch (e) { /* no URLSearchParams / quota */ }
  }

  function cookie(name) {
    try {
      var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) { return null; }
  }

  window.bqGetFbp = function () { return cookie('_fbp'); };

  // Raw GA4 cookies, parsed server-side. They let the server-sent generate_lead /
  // whatsapp_click join this visitor's session in GA4 and inherit its channel. The
  // second name is the web stream's measurement id (G-RKN671YBRW) minus "G-".
  // See ops/plans/ga4-lead-key-events.md.
  window.bqGetGa = function () {
    return { client: cookie('_ga'), session: cookie('_ga_RKN671YBRW') };
  };

  // The _fbc cookie exists only when the visitor arrived with ?fbclid=. When the pixel
  // hasn't written it (blocked, or the click landed before the pixel loaded) but we
  // captured the fbclid, build the value ourselves in Meta's documented format:
  // fb.<subdomainIndex>.<creationTime>.<fbclid>. Sending a synthesized fbc is supported
  // and is what keeps an ad click attributable through to the form submit.
  window.bqGetFbc = function () {
    var existing = cookie('_fbc');
    if (existing) return existing;
    if (!store) return null;
    try {
      var stored = store.getItem(FBCLID_KEY);
      if (!stored) return null;
      var sep = stored.indexOf('|');
      if (sep < 1) return null;
      return 'fb.1.' + stored.slice(0, sep) + '.' + stored.slice(sep + 1);
    } catch (e) { return null; }
  };
})();
